<template>
  <WrapperContent :className="'Page-Article'" page-article>
    <div class="Page-Article__body" ref="wrapperContentRef" v-if="get_isInfonoise && get_isPostStore">
      <BreadcrumbsMain class="Page-Article__breadcrumbs" :breadcrumbs="get_breadcrumbs"/>
      <ContentWidget :commentsPlace="'page'"/>
    </div>
    <div class="Page-Article__body" ref="wrapperContentRef" v-else-if="get_isSignals && get_isPostStore">
      <SignalContent :commentsPlace="'page'"/>
      <transition name="Anim__dropdown">
        <SignalFormPopup v-if="get_isOpenPopup"/>
      </transition>
    </div>
    <div class="Page-Article__loader" v-else>
      <LoaderCircle :color="'var(--circle-loader)'" :size="48"/>
    </div>
  </WrapperContent>
</template>

<script setup>
import {computed, onMounted, onBeforeUnmount, ref} from 'vue';
import {useStore} from 'vuex';
import {useRoute} from 'vue-router';
import {getPostData} from '@shared/api/posts/getPostData';
import {PATHS_NAME} from '@shared/routers/consts/pathsName';
import {throttle} from 'lodash';
import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName';
//------------------------------------
import SignalFormPopup from '@pages/Signals/components/FormPopup/SignalFormPopup.vue';
import LoaderCircle from '@ui/Loaders/LoaderCircle/LoaderCircle.vue';
import WrapperContent from '@ui/Wrappers/WrapperContent/WrapperContent.vue';
import BreadcrumbsMain from '@ui/Breadcrumbs/BreadcrumbsMain/BreadcrumbsMain.vue';
import ContentWidget from '@widgets/ContentWidget/widgets/MainContent/ContentWidget';
import SignalContent from '@widgets/ContentWidget/widgets/SignalContent/SignalContent.vue';
//------------------------------------
const store = useStore();
const route = useRoute();
//------------------------------------
const ID = route.params.id;
//------------------------------------
const wrapperContentRef = ref(null);
//------------------------------------
onMounted(async () => {
  window.addEventListener('scroll', handleScroll);
  if (get_isInfonoise.value) {
    await initPostData();
    updateMetaTags();
  } else if (get_isSignals.value) {
    toGetSignal();
  }
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});
//------------------------------------
//Эта страница относится к Инфошуму
const get_isInfonoise = computed(() => ['news', 'material', 'opinion'].includes(route.params.type));
//Эта страница относится к Сигналам
const get_isSignals = computed(() => ['signal'].includes(route.params.type));
//Получить сигнал
const get_signal = computed(() => store.state.Signals.data[ID]);
//Получить объект для передачи
const get_post = computed(() => {
  if (get_isInfonoise.value) {
    return store.state.ContentSystem.Alpha.list[ID] || store.state.ContentSystem.Infonoise.list[ID];
  } else if (get_isSignals.value) {
    return store.state.Signals.data[ID];
  }
});
//Проверить, есть ли данные о выбранном посте в словаре
const get_isPostStore = computed(() => {
  if (!get_post.value) return false;
  if (get_isInfonoise.value) {
    return (get_post.value['is_ready_inner_page']) ? true : false;
  } else if (get_isSignals.value) {
    return (get_signal.value) ? true : false;
  }
});
//Получить массив 'хлебных крошек'
const get_breadcrumbs = computed(() => {
  let sectionText = '';
  const type = route.params.type;

  if (type === 'news') sectionText = 'Инфошум';
  else if (type === 'material' || type === 'opinion') sectionText = 'Альфа';

  return [
    {text: sectionText, route: PATHS_NAME.MAIN},
    {text: get_post.value?.title || '', route: null},
  ];
});
//Идет ли подгрузка новых комментариев
const get_isLoadingNewComment = computed(() => store.state.ContentSystem.Comments['contentPageComments'].isLoadingNext);
//Готова ли ссылка для получения следующих комментариев
const get_isCommentsReady = computed(() => store.state.ContentSystem.Comments['contentPageComments'].nextPageUrl);
//Открыт ли попап редактирования сигналов
const get_isOpenPopup = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.signalForm));
//------------------------------------
//Инициализировать данные поста, если они отсутствуют в хранилище
const initPostData = async () => {
  if (get_isPostStore.value) return;
  const data = await getPostData(ID);
  const list = ['material', 'opinion'].includes(route.params.type) ?
    store.state.ContentSystem.Alpha.list :
    store.state.ContentSystem.Infonoise.list;
  list[ID] = data;
  list[ID]['is_ready_inner_page'] = true;
  return;
}
//Функция для обновления мета-тегов
const updateMetaTags = () => {
  document.title = getPlainText(get_post.value.title) || 'Drops Capital - Самое Боярское Криптосообщество';
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = get_post.value.title;
};
//Очистить текст от html тегов
const getPlainText = (html) => {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;
  return tempElement.textContent || tempElement.innerText || '';
};
//Проверка, достиг ли блок конца
const handleScroll = throttle(() => {
  if (!wrapperContentRef.value) return;
  const wrapper = wrapperContentRef.value.$el || wrapperContentRef.value;
  const rect = wrapper.getBoundingClientRect();
  const isAtBottom = rect.bottom - window.innerHeight <= 10;
  if (!isAtBottom) return;
  loadNextComments();
}, 300);
//Подгрузить новые комментарии
const loadNextComments = () => {
  if (get_isLoadingNewComment.value || !get_isCommentsReady.value) return;
  store.dispatch('ContentSystem/Comments/loadNextComments', {context: 'contentPageComments'});
};
//Если сигнала нет в хранилище, нужно получить его с сервера
const toGetSignal = async () => {
  if (get_signal.value) return;
  store.dispatch('Signals/getAndAddTheSignal', ID);
};
</script>

<style lang="scss">
@import "./styles/_style";
</style>
